import React, { useEffect, useState, type ReactElement } from "react";
import { Button } from "@ndd/ui";
import { shortDateLabel, localParts } from "../../../lib/rd-schedule-actions";
import type { ActionAppointment, ActionDialogContext } from "./action-types";
import { ActionDialogFrame, ActionForm } from "./action-frame";
import { useActionSubmit, useScheduleApi } from "./use-action-submit";

// NO-876. Delete (spec § Appointment actions; canvas Delete.dc.html): a hard
// delete with no undo and no email. The dialog points to Cancel as the usual
// choice.

export interface DeleteDialogProps extends ActionDialogContext {
  appointment: ActionAppointment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
  /** Opens Cancel or mark missed for the same visit. */
  onCancelInstead: () => void;
}

export function DeleteDialog(props: DeleteDialogProps): ReactElement {
  const [pending, setPending] = useState(false);
  const { appointment, timeZone } = props;
  const day = shortDateLabel(localParts(appointment.startsAt, timeZone).date);

  return (
    <ActionDialogFrame
      open={props.open}
      onOpenChange={props.onOpenChange}
      pending={pending}
      role="alertdialog"
      initialFocusId="delete-keep"
      title="Delete this appointment?"
      subtitle={
        <>
          {appointment.clientName}&apos;s {day} visit comes off your calendar and their
          record, with no undo, and {appointment.firstName} isn&apos;t told. To keep a
          record and email them,{" "}
          <Button
            type="button"
            variant="quiet-link"
            className="h-auto p-0 align-baseline"
            disabled={pending}
            onClick={props.onCancelInstead}
          >
            cancel it
          </Button>{" "}
          instead.
        </>
      }
    >
      <DeleteForm {...props} onPending={setPending} />
    </ActionDialogFrame>
  );
}

function DeleteForm(
  props: DeleteDialogProps & { onPending: (pending: boolean) => void }
): ReactElement {
  const { appointment, rdId, onChanged, onOpenChange, onPending } = props;
  const api = useScheduleApi();
  const action = useActionSubmit({
    api,
    rdId,
    timeZone: props.timeZone,
    onPartial: onChanged,
    onSucceeded: () => {
      onChanged();
      onOpenChange(false);
    },
  });

  useEffect(() => onPending(action.pending), [onPending, action.pending]);

  return (
    <ActionForm
      onSubmit={() =>
        void action.submit({ kind: "delete", appointmentId: appointment.id })
      }
      problem={action.problem}
      back={{
        id: "delete-keep",
        label: "Keep appointment",
        onClick: () => onOpenChange(false),
      }}
      confirm={{
        label: "Delete appointment",
        variant: "destructive",
        ...action.confirmState,
      }}
    >
      <></>
    </ActionForm>
  );
}
